import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { PurchaseBonus } from "@/lib/api";
import { takeWelcomeBonus, WELCOME_BONUS_EVENT } from "@/lib/welcomeBonus";

/** Shown once, right after signup, when the account already held purchased points. */
export default function WelcomeBonus() {
  const { t } = useTranslation();
  const [bonus, setBonus] = useState<PurchaseBonus | null>(() => takeWelcomeBonus());

  useEffect(() => {
    const show = () => setBonus(takeWelcomeBonus());
    window.addEventListener(WELCOME_BONUS_EVENT, show);
    return () => window.removeEventListener(WELCOME_BONUS_EVENT, show);
  }, []);

  if (!bonus) return null;
  const close = () => setBonus(null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-bonus-title"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/40 p-4"
      onClick={close}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-background-200/70 bg-background-50 p-6 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-gift-line text-2xl"></i>
        </div>
        <h2 id="welcome-bonus-title" className="mt-4 text-lg font-bold text-foreground-950">
          {t("auth.bonus.title")}
        </h2>
        <p className="mt-3 text-3xl font-extrabold text-primary-600">+{bonus.points}</p>
        <p className="mt-2 text-sm text-foreground-700">
          {t("auth.bonus.body", { count: bonus.purchases, points: bonus.points })}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-foreground-500">{t("auth.bonus.note")}</p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Link
            to="/generate"
            onClick={close}
            className="rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50"
          >
            {t("auth.bonus.cta")}
          </Link>
          <button
            type="button"
            onClick={close}
            className="rounded-full px-6 py-2.5 text-sm font-medium text-foreground-600 hover:bg-background-100 cursor-pointer"
          >
            {t("auth.bonus.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
